import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Palette, Sun, Moon, RotateCcw, Copy, Check, 
  Eye, Sparkles, Download, Upload
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';

// Default CSS variable values
const DEFAULT_LIGHT = {
  background: '210 40% 98%',
  foreground: '222 47% 11%',
  card: '0 0% 100%',
  'card-foreground': '222 47% 11%',
  primary: '262 83% 58%',
  'primary-foreground': '210 40% 98%',
  secondary: '210 40% 96.1%',
  'secondary-foreground': '222 47% 11%',
  muted: '210 40% 96.1%',
  'muted-foreground': '215.4 16.3% 46.9%',
  accent: '172 66% 50%',
  'accent-foreground': '222 47% 11%',
  destructive: '0 84.2% 60.2%',
  border: '214.3 31.8% 91.4%',
  ring: '262 83% 58%',
};

const DEFAULT_DARK = {
  background: '222 47% 11%',
  foreground: '210 40% 98%',
  card: '217 33% 17%',
  'card-foreground': '210 40% 98%',
  primary: '263 70% 60%',
  'primary-foreground': '210 40% 98%',
  secondary: '217.2 32.6% 17.5%',
  'secondary-foreground': '210 40% 98%',
  muted: '217.2 32.6% 17.5%',
  'muted-foreground': '215 20.2% 65.1%',
  accent: '172 66% 45%',
  'accent-foreground': '210 40% 98%',
  destructive: '0 62.8% 50%',
  border: '217.2 32.6% 25%',
  ring: '263 70% 60%',
};

// Preset themes
const PRESETS = {
  default: { name: 'Eva Default', light: DEFAULT_LIGHT, dark: DEFAULT_DARK },
  ocean: {
    name: 'Ocean Blue',
    light: { ...DEFAULT_LIGHT, primary: '200 80% 50%', accent: '180 60% 45%', ring: '200 80% 50%' },
    dark: { ...DEFAULT_DARK, primary: '200 80% 60%', accent: '180 60% 50%', ring: '200 80% 60%' },
  },
  forest: {
    name: 'Forest Green',
    light: { ...DEFAULT_LIGHT, primary: '142 70% 40%', accent: '160 60% 45%', ring: '142 70% 40%' },
    dark: { ...DEFAULT_DARK, primary: '142 70% 50%', accent: '160 60% 50%', ring: '142 70% 50%' },
  },
  sunset: {
    name: 'Sunset Orange',
    light: { ...DEFAULT_LIGHT, primary: '25 95% 55%', accent: '340 80% 60%', ring: '25 95% 55%' },
    dark: { ...DEFAULT_DARK, primary: '25 95% 60%', accent: '340 80% 65%', ring: '25 95% 60%' },
  },
  midnight: {
    name: 'Midnight Purple',
    light: { ...DEFAULT_LIGHT, primary: '280 70% 50%', accent: '320 70% 55%', ring: '280 70% 50%' },
    dark: { ...DEFAULT_DARK, primary: '280 70% 60%', accent: '320 70% 60%', ring: '280 70% 60%' },
  },
};

// Helper to convert HSL string to hex for color picker
function hslToHex(hsl) {
  const [h, s, l] = hsl.split(' ').map(v => parseFloat(v));
  const sNorm = s / 100;
  const lNorm = l / 100;
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = lNorm - c / 2;
  let r, g, b;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  const toHex = (n) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Helper to convert hex to HSL string
function hexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
      case g: h = ((b - r) / d + 2) * 60; break;
      case b: h = ((r - g) / d + 4) * 60; break;
      default: h = 0;
    }
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function ColorInput({ label, value, onChange, description }) {
  const [hexValue, setHexValue] = useState(() => {
    try { return hslToHex(value); } catch { return '#7c3aed'; }
  });
  const [copied, setCopied] = useState(false);

  // Update hex when HSL value changes from parent - intentional sync effect
  useEffect(() => {
    try { setHexValue(() => hslToHex(value)); } catch { /* ignore */ }
  }, [value]);

  const handleHexChange = (hex) => {
    setHexValue(hex);
    try {
      const hsl = hexToHsl(hex);
      onChange(hsl);
    } catch { /* ignore invalid hex */ }
  };

  const copyValue = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <button onClick={copyValue} className="text-muted-foreground hover:text-foreground">
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        </button>
      </div>
      <div className="flex gap-2">
        <div className="relative">
          <input
            type="color"
            value={hexValue}
            onChange={(e) => handleHexChange(e.target.value)}
            className="w-10 h-10 rounded-lg border border-border cursor-pointer"
          />
        </div>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="H S% L%"
          className="font-mono text-xs flex-1"
        />
      </div>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}

function PreviewCard({ colors }) {
  return (
    <div 
      className="rounded-xl border p-4 space-y-3"
      style={{ 
        backgroundColor: `hsl(${colors.card})`,
        borderColor: `hsl(${colors.border})`,
        color: `hsl(${colors['card-foreground']})`
      }}
    >
      <div className="flex items-center justify-between">
        <span className="font-semibold">Preview Card</span>
        <Badge style={{ backgroundColor: `hsl(${colors.primary})`, color: `hsl(${colors['primary-foreground']})` }}>
          Badge
        </Badge>
      </div>
      <p style={{ color: `hsl(${colors['muted-foreground']})` }} className="text-sm">
        This is how your theme will look across the application.
      </p>
      <div className="flex gap-2">
        <button
          className="px-3 py-1.5 rounded-md text-sm font-medium"
          style={{ backgroundColor: `hsl(${colors.primary})`, color: `hsl(${colors['primary-foreground']})` }}
        >
          Primary
        </button>
        <button
          className="px-3 py-1.5 rounded-md text-sm font-medium border"
          style={{ 
            backgroundColor: `hsl(${colors.secondary})`, 
            color: `hsl(${colors['secondary-foreground']})`,
            borderColor: `hsl(${colors.border})`
          }}
        >
          Secondary
        </button>
        <button
          className="px-3 py-1.5 rounded-md text-sm font-medium"
          style={{ backgroundColor: `hsl(${colors.accent})`, color: `hsl(${colors['accent-foreground']})` }}
        >
          Accent
        </button>
      </div>
    </div>
  );
}

export default function ThemeCustomizerPage() {
  const { theme } = useTheme();
  const { isSuperadmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('light');
  const [lightColors, setLightColors] = useState(DEFAULT_LIGHT);
  const [darkColors, setDarkColors] = useState(DEFAULT_DARK);
  const [hasChanges, setHasChanges] = useState(false);

  // Redirect non-superadmins to overview
  useEffect(() => {
    if (!isSuperadmin) {
      navigate('/overview');
    }
  }, [isSuperadmin, navigate]);

  // Load saved theme from localStorage on mount - initialize state from storage
  useEffect(() => {
    const saved = localStorage.getItem('eva_custom_theme');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Intentional setState for persisted state restoration
        if (parsed.light) setLightColors(() => parsed.light);
        if (parsed.dark) setDarkColors(() => parsed.dark);
      } catch { /* ignore */ }
    }
  }, []);

  // Apply colors to CSS variables in real-time
  useEffect(() => {
    const root = document.documentElement;
    const allKeys = Object.keys(DEFAULT_LIGHT);

    // When activeTab matches the current theme, apply custom colors as inline overrides
    if ((theme === 'light' && activeTab === 'light') || (theme === 'dark' && activeTab === 'dark')) {
      const colors = activeTab === 'light' ? lightColors : darkColors;
      Object.entries(colors).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
      });
    } else {
      // When activeTab doesn't match theme, remove inline overrides so the
      // stylesheet's .dark/:root rules apply correctly
      allKeys.forEach(key => root.style.removeProperty(`--${key}`));
    }

    // Cleanup: remove all inline overrides when leaving the page
    return () => {
      allKeys.forEach(key => root.style.removeProperty(`--${key}`));
    };
  }, [lightColors, darkColors, activeTab, theme]);

  // Don't render anything for non-superadmins
  if (!isSuperadmin) {
    return null;
  }

  const updateColor = (mode, key, value) => {
    setHasChanges(true);
    if (mode === 'light') {
      setLightColors(prev => ({ ...prev, [key]: value }));
    } else {
      setDarkColors(prev => ({ ...prev, [key]: value }));
    }
  };

  const applyPreset = (presetKey) => {
    const preset = PRESETS[presetKey];
    setLightColors(preset.light);
    setDarkColors(preset.dark);
    setHasChanges(true);
    toast.success(`Applied "${preset.name}" preset`);
  };

  const resetToDefault = () => {
    setLightColors(DEFAULT_LIGHT);
    setDarkColors(DEFAULT_DARK);
    setHasChanges(true);
    
    // Clear CSS overrides
    const root = document.documentElement;
    Object.keys(DEFAULT_LIGHT).forEach(key => {
      root.style.removeProperty(`--${key}`);
    });
    
    toast.success('Reset to default theme');
  };

  const saveTheme = () => {
    const themeData = { light: lightColors, dark: darkColors };
    localStorage.setItem('eva_custom_theme', JSON.stringify(themeData));
    setHasChanges(false);
    toast.success('Theme saved successfully');
  };

  const exportTheme = () => {
    const themeData = { light: lightColors, dark: darkColors };
    const blob = new Blob([JSON.stringify(themeData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'eva-theme.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Theme exported');
  };

  const importTheme = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (data.light) setLightColors(data.light);
        if (data.dark) setDarkColors(data.dark);
        setHasChanges(true);
        toast.success('Theme imported');
      } catch {
        toast.error('Invalid theme file');
      }
    };
    reader.readAsText(file);
  };

  const currentColors = activeTab === 'light' ? lightColors : darkColors;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="theme-customizer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
            <Palette className="w-8 h-8 text-primary" />
            Theme Customizer
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Customize the application&apos;s color scheme for all users
          </p>
        </div>
        <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
          Superadmin Only
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Presets & Actions */}
        <div className="space-y-4">
          <Card className="card-elevated border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Quick Presets
              </CardTitle>
              <CardDescription>Apply a pre-designed color scheme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  onClick={() => applyPreset(key)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/80 transition-colors text-left"
                >
                  <div className="flex gap-1">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: `hsl(${preset.light.primary})` }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: `hsl(${preset.light.accent})` }}
                    />
                  </div>
                  <span className="font-medium text-foreground">{preset.name}</span>
                </button>
              ))}
            </CardContent>
          </Card>

          <Card className="card-elevated border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button onClick={saveTheme} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" disabled={!hasChanges}>
                <Check className="w-4 h-4 mr-2" />
                Save Theme
              </Button>
              <Button onClick={resetToDefault} variant="outline" className="w-full">
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Default
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={exportTheme} variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
                <label className="cursor-pointer">
                  <Button variant="outline" size="sm" className="w-full" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-1" />
                      Import
                    </span>
                  </Button>
                  <input type="file" accept=".json" onChange={importTheme} className="hidden" />
                </label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Center: Color Editor */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="card-elevated border-0">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Color Editor
                </CardTitle>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger value="light" className="gap-1">
                      <Sun className="w-4 h-4" />
                      Light
                    </TabsTrigger>
                    <TabsTrigger value="dark" className="gap-1">
                      <Moon className="w-4 h-4" />
                      Dark
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              {/* Preview */}
              <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: `hsl(${currentColors.background})` }}>
                <PreviewCard colors={currentColors} />
              </div>

              {/* Color Inputs */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <ColorInput
                  label="Background"
                  value={currentColors.background}
                  onChange={(v) => updateColor(activeTab, 'background', v)}
                  description="Main page background"
                />
                <ColorInput
                  label="Foreground"
                  value={currentColors.foreground}
                  onChange={(v) => updateColor(activeTab, 'foreground', v)}
                  description="Primary text color"
                />
                <ColorInput
                  label="Primary"
                  value={currentColors.primary}
                  onChange={(v) => updateColor(activeTab, 'primary', v)}
                  description="Buttons, links, accents"
                />
                <ColorInput
                  label="Secondary"
                  value={currentColors.secondary}
                  onChange={(v) => updateColor(activeTab, 'secondary', v)}
                  description="Secondary backgrounds"
                />
                <ColorInput
                  label="Accent"
                  value={currentColors.accent}
                  onChange={(v) => updateColor(activeTab, 'accent', v)}
                  description="Highlights, badges"
                />
                <ColorInput
                  label="Muted"
                  value={currentColors.muted}
                  onChange={(v) => updateColor(activeTab, 'muted', v)}
                  description="Muted backgrounds"
                />
                <ColorInput
                  label="Muted Text"
                  value={currentColors['muted-foreground']}
                  onChange={(v) => updateColor(activeTab, 'muted-foreground', v)}
                  description="Secondary text"
                />
                <ColorInput
                  label="Card"
                  value={currentColors.card}
                  onChange={(v) => updateColor(activeTab, 'card', v)}
                  description="Card backgrounds"
                />
                <ColorInput
                  label="Border"
                  value={currentColors.border}
                  onChange={(v) => updateColor(activeTab, 'border', v)}
                  description="Borders, dividers"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
